import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Loader2, Download, Film, Clock, Wand2, Coins, ShieldCheck, ShoppingCart, CheckCircle, Plus, X, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const SIZES = [
  { label: 'Landscape (HD)', value: '1280x720' },
  { label: 'Widescreen', value: '1792x1024' },
  { label: 'Portrait', value: '1024x1792' },
  { label: 'Square', value: '1024x1024' },
];
const DURATIONS = [4, 8, 12];

export default function VideoGeneratorPage() {
  const { user } = useAuth();
  const [prompt, setPrompt] = useState('');
  const [size, setSize] = useState('1280x720');
  const [duration, setDuration] = useState(4);
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [failError, setFailError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [credits, setCredits] = useState(null);
  const [buying, setBuying] = useState(false);
  const pollRef = useRef(null);
  const fileRef = useRef(null);

  // Upload state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadedFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setUploadPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const clearUpload = () => {
    setUploadedFile(null);
    setUploadPreview(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  useEffect(() => {
    loadCredits();
    // Check for credit purchase return
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('session_id');
    if (sid) {
      api.post('/credits/fulfill', { session_id: sid }).then(r => {
        if (r.data.credits_added) toast.success(`${r.data.credits_added} credits added!`);
        loadCredits();
        window.history.replaceState({}, '', '/video-generator');
      }).catch(() => {});
    }
  }, []);

  const loadCredits = () => {
    api.get('/credits/balance').then(r => setCredits(r.data)).catch(() => {});
  };

  const canGenerate = true; // Universal Key handles billing directly
  const isAdminTest = false;

  const generate = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setVideoUrl(null);
    setStatus(null);
    setFailError(null);
    try {
      let res;
      if (uploadedFile) {
        // Image-to-video mode
        const fd = new FormData();
        fd.append('file', uploadedFile);
        fd.append('prompt', prompt.trim());
        fd.append('size', size);
        fd.append('duration', String(duration));
        res = await api.post('/generate/video/from-image', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 30000,
        });
      } else {
        res = await api.post('/generate/video', { prompt: prompt.trim(), size, duration });
      }
      setJobId(res.data.job_id);
      setStatus('processing');
      setPolling(true);
      loadCredits();
      toast.success('Video generation started — uses Universal Key credits');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start video generation');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!polling || !jobId) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/generate/video/status/${jobId}`);
        setStatus(res.data.status);
        if (res.data.status === 'completed') {
          setVideoUrl(res.data.video_url);
          setPolling(false);
          clearInterval(pollRef.current);
          toast.success('Video ready! Auto-saved to your storage.');
          loadCredits();
        } else if (res.data.status === 'failed') {
          setPolling(false);
          clearInterval(pollRef.current);
          setFailError(res.data.error);
          toast.error(res.data.error || 'Video generation failed');
          loadCredits();
        }
      } catch { /* keep polling */ }
    }, 8000);
    return () => clearInterval(pollRef.current);
  }, [polling, jobId]);

  return (
    <div className="max-w-3xl mx-auto animate-in" data-testid="video-generator-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 bg-gold/10 flex items-center justify-center">
          <Film size={18} className="text-gold" />
        </div>
        <div>
          <h1 className="font-serif text-2xl md:text-3xl">AI Video Creator</h1>
          <p className="text-sm text-muted-foreground">Create videos from text or animate an uploaded image</p>
        </div>
      </div>

      {/* Universal Key Status */}
      <div className="border border-emerald-200/50 bg-emerald-50/30 p-4 mt-4 flex items-center gap-3 rounded-xl" data-testid="credit-banner">
        <CheckCircle size={16} className="text-emerald-600" />
        <div>
          <p className="text-xs font-medium text-emerald-800" data-testid="credit-balance">Universal Key Connected</p>
          <p className="text-[10px] text-emerald-700/70">Video generation uses your Universal Key credits directly</p>
        </div>
      </div>

      {/* Upload Zone */}
      <div className="border border-border/40 p-5 mt-4 mb-0 rounded-t-xl" data-testid="video-upload-zone">
        <div className="flex items-center justify-between mb-3">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Source Image (optional)</label>
          {uploadPreview && (
            <button onClick={clearUpload} className="text-[10px] text-red-500 hover:text-red-700 flex items-center gap-1">
              <X size={10} /> Remove
            </button>
          )}
        </div>
        {uploadPreview ? (
          <div className="flex items-start gap-4">
            <img src={uploadPreview} alt="Upload" className="w-24 h-24 rounded-xl object-cover border border-border/30 shadow-sm" data-testid="video-upload-preview" />
            <div className="flex-1 pt-1">
              <p className="text-xs font-medium text-obsidian">{uploadedFile?.name}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{(uploadedFile?.size / 1024).toFixed(0)} KB</p>
              <p className="text-[10px] text-rose-600 mt-2 font-medium">AI will animate this image into a video</p>
            </div>
          </div>
        ) : (
          <button
            onClick={() => fileRef.current?.click()}
            className="w-full border-2 border-dashed border-rose-200 rounded-xl py-6 flex flex-col items-center gap-2 hover:border-rose-400 hover:bg-rose-50/30 transition-all cursor-pointer"
            data-testid="video-upload-btn"
          >
            <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
              <Plus size={20} className="text-rose-500" />
            </div>
            <p className="text-xs font-medium text-obsidian">Upload an image to animate</p>
            <p className="text-[10px] text-muted-foreground">or skip to create from text only</p>
          </button>
        )}
        <input ref={fileRef} type="file" className="hidden" accept="image/*" onChange={handleUpload} />
      </div>

      {/* Prompt Area */}
      <div className="border border-t-0 border-border/40 p-6 mb-6 rounded-b-xl">
        <label className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-2">
          {uploadedFile ? 'Describe the video motion for this image' : 'Describe your video'}
        </label>
        <Textarea
          data-testid="video-gen-prompt"
          placeholder={uploadedFile
            ? 'e.g. Slow zoom into this wallpaper with gentle light animation...'
            : 'A cat playing piano in a cozy jazz club with warm lighting...'
          }
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          className="rounded-lg border-border/40 bg-transparent px-3 py-2 shadow-none focus-visible:ring-1 focus-visible:ring-rose-300 resize-none text-sm min-h-[60px]"
          rows={3}
        />

        <label className="text-[10px] uppercase tracking-wider text-muted-foreground block mt-5 mb-3">Format</label>
        <div className="flex flex-wrap gap-2" data-testid="size-picker">
          {SIZES.map(s => (
            <button key={s.value} onClick={() => setSize(s.value)} className={`px-3 py-1.5 text-xs border transition-all duration-200 ${size === s.value ? 'bg-obsidian text-white border-obsidian' : 'border-border/40 hover:border-gold/50 text-muted-foreground'}`} data-testid={`size-${s.value}`}>
              {s.label}
            </button>
          ))}
        </div>

        <label className="text-[10px] uppercase tracking-wider text-muted-foreground block mt-5 mb-3">Duration</label>
        <div className="flex gap-2" data-testid="duration-picker">
          {DURATIONS.map(d => (
            <button key={d} onClick={() => setDuration(d)} className={`px-4 py-1.5 text-xs border transition-all duration-200 ${duration === d ? 'bg-obsidian text-white border-obsidian' : 'border-border/40 hover:border-gold/50 text-muted-foreground'}`} data-testid={`duration-${d}`}>
              {d}s
            </button>
          ))}
        </div>

        <Button
          data-testid="generate-video-btn"
          onClick={generate}
          disabled={!prompt.trim() || loading || polling || !canGenerate}
          className="w-full mt-6 rounded-xl h-12 uppercase tracking-wider text-xs bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 text-white transition-all duration-300 disabled:opacity-40"
        >
          {loading ? <><Loader2 size={14} className="mr-2 animate-spin" /> Starting...</> :
           polling ? <><Clock size={14} className="mr-2 animate-pulse" /> Generating...</> :
           !canGenerate ? <><Coins size={14} className="mr-2" /> Insufficient Credits</> :
           uploadedFile ? <><Wand2 size={14} className="mr-2" /> Create Video from Image</> :
           <><Wand2 size={14} className="mr-2" /> Generate Video</>}
        </Button>
      </div>

      {/* Progress */}
      {polling && (
        <div className="border border-gold/30 bg-gold/5 p-6 mb-6 animate-in text-center" data-testid="video-progress">
          <Loader2 size={32} className="mx-auto text-gold animate-spin mb-3" />
          <p className="text-sm font-medium">Generating your video...</p>
          <p className="text-xs text-muted-foreground mt-1">This takes 2-5 minutes. Video auto-saves to your storage.</p>
          <div className="mt-4 h-1 bg-border/30 overflow-hidden">
            <div className="h-full bg-gold/60 animate-pulse" style={{ width: '60%' }} />
          </div>
        </div>
      )}

      {/* Result */}
      {videoUrl && (
        <div className="border border-border/40 overflow-hidden mb-8 animate-in" data-testid="generated-video-result">
          <video src={videoUrl} controls className="w-full" data-testid="generated-video" />
          <div className="p-4 flex items-center justify-between bg-mist/30">
            <div className="flex items-center gap-2">
              <CheckCircle size={14} className="text-green-600" />
              <p className="text-xs text-muted-foreground">Saved to your storage</p>
            </div>
            <Button data-testid="download-video-btn" variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => { const a = document.createElement('a'); a.href = videoUrl; a.download = `ai-video-${Date.now()}.mp4`; a.click(); }}>
              <Download size={12} className="mr-1" /> Download
            </Button>
          </div>
        </div>
      )}

      {status === 'failed' && !videoUrl && (
        <div className="border border-destructive/30 bg-destructive/5 p-6 text-center" data-testid="video-error">
          <p className="text-sm text-destructive mb-2">{failError || 'Generation failed. Credits have been refunded.'}</p>
          {failError?.includes('balance') && (
            <p className="text-xs text-muted-foreground">Go to <strong>Profile &gt; Universal Key &gt; Add Balance</strong> on the Emergent platform to recharge.</p>
          )}
        </div>
      )}
    </div>
  );
}
